// arEmailCollectionsStore.ts
import { create } from 'zustand';
import { ArEmailQueueItem, ArEmailQueueMatter } from '@xtnd/web/features/ar-collections/types';

type MaybeArray<T> = T | T[];
interface ArEmailCollectionsState {
  clients: ArEmailQueueItem[];
  matters: ArEmailQueueMatter[];
  selectedKeys: string[];
  isSending: boolean;

  setClients: (value: MaybeArray<ArEmailQueueItem>) => void;
  setMatters: (value: MaybeArray<ArEmailQueueMatter>) => void;
  updateMatters: (value: MaybeArray<ArEmailQueueMatter>) => void;
  setSelectedKeys: (value: MaybeArray<string>) => void;
  updateSelectedKeys: (value: MaybeArray<string>) => void;
  setIsSending: (value: boolean) => void;
  resetAll: () => void;
}
const normalizeArray = <T>(value: T | T[]): T[] => (Array.isArray(value) ? value : [value]);

export const useArEmailCollectionsStore = create<ArEmailCollectionsState>()(set => ({
  clients: [],
  matters: [],
  selectedKeys: [],
  isSending: false,

  setClients: value =>
    set(state => ({
      ...state,
      clients: normalizeArray(value),
    })),

  setMatters: value =>
    set(state => ({
      ...state,
      matters: normalizeArray(value),
    })),

  updateMatters: value => {
    const valueUpdate = normalizeArray(value);
    const filterMatters = (matters: ArEmailQueueMatter[]) =>
      matters.filter(item => !valueUpdate.some(v => v.matterId === item.matterId));

    const removedMatterIds = new Set(valueUpdate.map(v => v.matterId));
    const filterClients = (clients: ArEmailQueueItem[]) => {
      return clients
        .map(client => {
          const matters = (client.matters ?? []).filter(m => !removedMatterIds.has(m.matterId));

          return {
            ...client,
            matters,
          };
        })
        .filter(client => client.matters.length > 0);
    };

    set(state => ({
      ...state,
      matters: filterMatters(state.matters),
      clients: filterClients(state.clients),
    }));
  },

  setSelectedKeys: value => {
    set(state => ({
      ...state,
      selectedKeys: normalizeArray(value),
    }));
  },

  updateSelectedKeys: value => {
    set(state => {
      const keyUpdate = state.selectedKeys.filter(key => key !== value);
      return {
        ...state,
        selectedKeys: [...keyUpdate],
      };
    });
  },

  setIsSending: value =>
    set(state => ({
      ...state,
      isSending: value,
    })),

  resetAll: () =>
    set({
      clients: [],
      matters: [],
      selectedKeys: [],
      isSending: false,
    }),
}));
